import type { WeatherIconKind } from './weatherCondition';

type WeatherIconProps = {
  kind: WeatherIconKind;
  className?: string;
};

function SunCore({ cx, cy, r }: { cx: number; cy: number; r: number }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="url(#wiSun)" />
      <g stroke="#f59e0b" strokeWidth={2.4} strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={`0 ${cx} ${cy}`}
          to={`360 ${cx} ${cy}`}
          dur="24s"
          repeatCount="indefinite"
        />
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i * Math.PI) / 4;
          return (
            <line
              key={i}
              x1={cx + Math.cos(a) * (r + 3.5)}
              y1={cy + Math.sin(a) * (r + 3.5)}
              x2={cx + Math.cos(a) * (r + 8)}
              y2={cy + Math.sin(a) * (r + 8)}
            />
          );
        })}
      </g>
    </g>
  );
}

function CloudShape({ fill, drift = 0 }: { fill: string; drift?: number }) {
  return (
    <g>
      {drift > 0 && (
        <animateTransform
          attributeName="transform"
          type="translate"
          values={`0 0; ${drift} 0; 0 0`}
          dur="6s"
          repeatCount="indefinite"
        />
      )}
      <path
        d="M16 40a9 9 0 0 1 1.5-17.9 12 12 0 0 1 23.4-2.6A10 10 0 1 1 44 40H16z"
        fill={fill}
      />
    </g>
  );
}

function RainDrops({
  count,
  color,
  dur,
}: {
  count: number;
  color: string;
  dur: string;
}) {
  const xs = [20, 30, 40, 25, 35].slice(0, count);
  return (
    <g>
      {xs.map((x, i) => (
        <line
          key={i}
          x1={x}
          y1={45}
          x2={x - 2}
          y2={51}
          stroke={color}
          strokeWidth={2.4}
          strokeLinecap="round"
        >
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 -3; 0 7"
            dur={dur}
            begin={`${i * 0.35}s`}
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            dur={dur}
            begin={`${i * 0.35}s`}
            repeatCount="indefinite"
          />
        </line>
      ))}
    </g>
  );
}

const gradients = (
  <defs>
    <radialGradient id="wiSun">
      <stop offset="0%" stopColor="#fde68a" />
      <stop offset="100%" stopColor="#f59e0b" />
    </radialGradient>
    <linearGradient id="wiCloud" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#f1f5f9" />
      <stop offset="100%" stopColor="#94a3b8" />
    </linearGradient>
    <linearGradient id="wiCloudDark" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#94a3b8" />
      <stop offset="100%" stopColor="#475569" />
    </linearGradient>
    <linearGradient id="wiCloudStorm" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#64748b" />
      <stop offset="100%" stopColor="#1e293b" />
    </linearGradient>
  </defs>
);

export function WeatherIcon({ kind, className }: WeatherIconProps) {
  if (kind === 'unknown') {
    return (
      <div
        className={`flex items-center justify-center text-slate-300 dark:text-slate-600 ${className ?? ''}`}
      >
        <span className="text-lg leading-none">—</span>
      </div>
    );
  }

  return (
    <svg
      viewBox="0 0 60 60"
      className={className}
      role="img"
      aria-label={kind.replace('-', ' ')}
    >
      {gradients}
      {kind === 'sunny' && <SunCore cx={30} cy={30} r={11} />}
      {kind === 'partly-cloudy' && (
        <>
          <SunCore cx={22} cy={22} r={9} />
          <g transform="translate(6 6) scale(0.85)">
            <CloudShape fill="url(#wiCloud)" drift={2.5} />
          </g>
        </>
      )}
      {kind === 'cloudy' && (
        <>
          <g transform="translate(8 -4) scale(0.7)" opacity={0.7}>
            <CloudShape fill="url(#wiCloudDark)" />
          </g>
          <CloudShape fill="url(#wiCloud)" drift={3} />
        </>
      )}
      {(kind === 'drizzle' || kind === 'rainy') && (
        <>
          <CloudShape
            fill={kind === 'rainy' ? 'url(#wiCloudDark)' : 'url(#wiCloud)'}
          />
          <RainDrops
            count={kind === 'rainy' ? 3 : 2}
            color="#3b82f6"
            dur={kind === 'rainy' ? '1s' : '1.6s'}
          />
        </>
      )}
      {kind === 'thunderstorm' && (
        <>
          <CloudShape fill="url(#wiCloudStorm)" />
          <RainDrops count={2} color="#3b82f6" dur="0.9s" />
          <path
            d="M31 38l-5 9h4l-3 9 8-11h-4l4-7z"
            fill="#facc15"
            stroke="#eab308"
            strokeWidth={0.5}
          >
            <animate
              attributeName="opacity"
              values="1;1;0.2;1;1"
              keyTimes="0;0.45;0.5;0.55;1"
              dur="2.6s"
              repeatCount="indefinite"
            />
          </path>
        </>
      )}
      {kind === 'snow' && (
        <>
          <CloudShape fill="url(#wiCloud)" />
          {[22, 31, 40].map((x, i) => (
            <circle key={i} cx={x} cy={47} r={1.8} fill="#93c5fd">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0 -3; 0 7"
                dur="2.4s"
                begin={`${i * 0.6}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                dur="2.4s"
                begin={`${i * 0.6}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}
        </>
      )}
      {kind === 'fog' && (
        <>
          <g transform="translate(0 -6)">
            <CloudShape fill="url(#wiCloud)" />
          </g>
          {[40, 46, 52].map((y, i) => (
            <line
              key={i}
              x1={14}
              y1={y}
              x2={46}
              y2={y}
              stroke="#94a3b8"
              strokeWidth={2.4}
              strokeLinecap="round"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0 0; ${i % 2 === 0 ? 4 : -4} 0; 0 0`}
                dur="5s"
                repeatCount="indefinite"
              />
            </line>
          ))}
        </>
      )}
    </svg>
  );
}
